import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../components/Breadcrumbs";
import CardPlaceholder from "../components/CardPlaceholder";
import UploadModal from "../components/UploadModal";
import Showcase from "../components/Showcase";
import Pagination from "../components/Pagination";
import Lightbox from "../components/Lightbox";
import {
  getCard,
  getCardInSetByNumber,
  getCardVariant,
  getMyCardSubmission,
  getSetBySlug,
  listCardShowcases,
} from "../lib/api";
import { useAuth } from "../lib/useAuth";
import { formatCardNumber } from "../lib/cardNumber";
import { cardPath, setPath, showcasePath } from "../lib/slug";
import { cardMeta, breadcrumbList, productLD } from "../lib/seo";

/**
 * CardDetailPage — canonical URL (owner directive 2026-02-01):
 *
 *   /{game}/{lang}/sets/{set-slug}/{card-slug}/{number}[/{variant}]
 *
 * Resolution keys on {set-slug, number} — NEVER on {card-slug}.
 *
 * Bare URL (no variant segment) = the card's DEFAULT printing per
 * Phase 7 precedence.
 *
 * SEO pass (2026-02-01):
 *   - <Helmet> for client-side head updates.
 *   - JSON-LD Product (with Offer only when a real price exists) +
 *     BreadcrumbList.
 *   - Initial document render is meta-injected by the backend.
 */
export default function CardDetailPage() {
  const {
    game,
    lang,
    setSlug,
    cardSlug,
    number,
    "*": splat,
  } = useParams();
  const variantSlug = splat && splat.length > 0 ? splat.split("/")[0] : undefined;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Math.max(
    1,
    parseInt(searchParams.get("page") || "1", 10) || 1,
  );

  const [detail, setDetail] = useState(null);
  const [detailState, setDetailState] = useState("loading");
  const [detailError, setDetailError] = useState(null);
  const [resolvedExternalId, setResolvedExternalId] = useState(null);

  const auth = useAuth();
  const [mine, setMine] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const [showPage, setShowPage] = useState(initialPage);
  const [showData, setShowData] = useState(null);
  const [showState, setShowState] = useState("loading");
  const [refLightbox, setRefLightbox] = useState(false);

  // ---- Resolve card + variant payload ----
  useEffect(() => {
    let alive = true;
    setDetailState("loading");
    setDetailError(null);
    (async () => {
      try {
        let setDoc = null;
        try {
          setDoc = await getSetBySlug(setSlug);
        } catch {
          const { getSet } = await import("../lib/api");
          setDoc = await getSet(setSlug);
        }
        const card = await getCardInSetByNumber(setDoc.external_id, number);
        const defaultVariantSlug = card.default_variant_slug || null;
        const wantedVariantSlug = variantSlug || defaultVariantSlug;

        const canonical = cardPath({
          game,
          lang,
          setName: setDoc.name || setDoc.external_id,
          cardName: card.name || cardSlug,
          number: card.number,
          variantSlug: wantedVariantSlug,
          defaultVariantSlug,
        });
        const currentPath = `/${game}/${lang}/sets/${setSlug}/${cardSlug}/${number}${
          variantSlug ? `/${variantSlug}` : ""
        }`;
        if (canonical !== currentPath) {
          const qs = window.location.search.replace(/^\?/, "");
          if (!alive) return;
          navigate(canonical + (qs ? `?${qs}` : ""), { replace: true });
          return;
        }
        setResolvedExternalId(card.external_id);

        const payload = await getCardVariant(
          card.external_id,
          wantedVariantSlug,
        );
        // No `alive` guard here — React 18 Strict Mode fires cleanup between
        // mount double-invocations. Duplicate setState with same shape is a
        // cheap no-op.
        setDetail(payload);
        setDetailState("ready");
      } catch (e) {
        if (!alive) return;
        setDetailError(
          e?.response?.data?.detail || e?.message || "Card not found",
        );
        setDetailState("error");
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, lang, setSlug, cardSlug, number, variantSlug]);

  useEffect(() => { setShowPage(1); }, [variantSlug]);

  const activeVariantSlug = detail?.variant?.slug || null;
  const loadMine = useCallback(async () => {
    if (auth.loading) return;
    if (!auth.user || !resolvedExternalId || !activeVariantSlug) { setMine(null); return; }
    try {
      const sub = await getMyCardSubmission(resolvedExternalId, activeVariantSlug);
      setMine(sub);
    } catch (_e) { setMine(null); }
  }, [auth.user, auth.loading, resolvedExternalId, activeVariantSlug]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const loadShowcases = useCallback(async () => {
    if (!resolvedExternalId || !activeVariantSlug) return;
    setShowState("loading");
    try {
      const data = await listCardShowcases(resolvedExternalId, activeVariantSlug, { page: showPage });
      setShowData(data);
      setShowState("ready");
    } catch (_e) {
      setShowState("error");
    }
  }, [resolvedExternalId, activeVariantSlug, showPage]);
  useEffect(() => { loadShowcases(); }, [loadShowcases, auth.user?.id]);

  // ---- SEO meta + JSON-LD (memoised on payload changes) ----
  const meta = useMemo(() => {
    if (!detail?.card || !detail?.set || !detail?.variant) return null;
    return cardMeta({
      setDoc: detail.set,
      card: detail.card,
      variant: detail.variant,
      defaultVariantSlug: detail.default_variant_slug || null,
    });
  }, [detail]);

  const jsonldItems = useMemo(() => {
    if (!detail?.card || !detail?.set || !detail?.variant || !meta) return null;
    const variantSlugActive = detail.variant.slug || detail.variant.variant_type || "";
    const isDefault =
      detail.default_variant_slug && variantSlugActive === detail.default_variant_slug;
    const variantLabel = detail.variant.variant_label;
    const nameSuffix = isDefault || !variantLabel ? "" : ` · ${variantLabel}`;
    const breadcrumb = breadcrumbList({
      items: [
        { name: "Pokémon", path: "/pokemon" },
        { name: "English Sets", path: "/pokemon/en/sets" },
        { name: detail.set.name, path: setPath(game, lang, detail.set.name || detail.set.external_id) },
        { name: (detail.card.name || "") + nameSuffix, path: null },
      ],
    });
    const product = productLD({
      setDoc: detail.set,
      card: detail.card,
      variant: detail.variant,
      defaultVariantSlug: detail.default_variant_slug || null,
      meta,
    });
    return [product, breadcrumb];
  }, [detail, meta, game, lang]);

  function goPage(n) {
    setShowPage(n);
    const next = new URLSearchParams(searchParams);
    if (n > 1) next.set("page", String(n));
    else next.delete("page");
    setSearchParams(next, { replace: false });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const card = detail?.card;
  const set = detail?.set;
  const variant = detail?.variant;
  const siblings = (detail?.siblings || []).filter((s) => !s.deprecated);
  const results = showData?.results || [];
  const total = showData?.total || 0;
  const totalPages = showData?.total_pages || 0;
  const hero = showPage === 1 && results.length >= 1 ? results[0] : null;
  const rest = showPage === 1 ? results.slice(1) : results;

  return (
    <div
      className="mx-auto w-full max-w-[1400px] px-5 py-8 md:px-gutter md:py-10"
      data-testid="card-detail-page"
    >
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <meta name="robots" content={meta.robots} />
          <link rel="canonical" href={meta.canonical} />
          <meta property="og:site_name" content="CardCapture" />
          <meta property="og:title" content={meta.title} />
          <meta property="og:description" content={meta.description} />
          <meta property="og:url" content={meta.canonical} />
          <meta property="og:type" content={meta.ogType} />
          <meta property="og:image" content={meta.ogImage} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={meta.title} />
          <meta name="twitter:description" content={meta.description} />
          <meta name="twitter:image" content={meta.ogImage} />
          {jsonldItems &&
            jsonldItems.map((obj) => (
              <script key={obj["@type"] || JSON.stringify(obj).slice(0, 32)} type="application/ld+json">
                {JSON.stringify(obj)}
              </script>
            ))}
        </Helmet>
      )}

      {set && card ? (
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Pokémon", to: "/pokemon" },
            { label: "English", to: "/pokemon/en/sets" },
            { label: set.name, to: setPath(game, lang, set.name || set.external_id) },
            {
              label:
                `#${card.number} · ${card.name}` +
                (variant?.variant_label ? ` · ${variant.variant_label}` : ""),
            },
          ]}
        />
      ) : (
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Card" }]} />
      )}

      {detailState === "loading" && (
        <div className="mt-16 text-body text-ink-muted" data-testid="card-loading">Loading card…</div>
      )}
      {detailState === "error" && (
        <div className="mt-16 text-body text-ink-muted" data-testid="card-error">{detailError}</div>
      )}

      {detailState === "ready" && card && variant && (
        <div className="mt-6" data-testid="card-detail-body">
          <CardIdentityHeader
            card={card}
            variant={variant}
            set={set}
            game={game}
            lang={lang}
            onOpenLightbox={() => setRefLightbox(true)}
          />

          {siblings.length > 0 && (
            <OtherPrintings
              game={game}
              lang={lang}
              setName={set.name || set.external_id}
              cardName={card.name}
              number={card.number}
              defaultVariantSlug={detail.default_variant_slug || null}
              siblings={siblings}
              activeSlug={variant.slug}
            />
          )}

          <section className="mt-8" data-testid="showcases-panel">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <div className="text-eyebrow uppercase text-ink-muted">Community</div>
                <h2 className="mt-1 text-title-section text-ink-text">
                  Showcases
                  {total > 0 && (
                    <span className="ml-2 text-meta text-ink-muted tabular" data-testid="showcases-total">
                      · {total}
                    </span>
                  )}
                </h2>
              </div>
              {auth.user && !mine && (
                <button
                  data-testid="add-showcase-cta"
                  onClick={() => setUploadOpen(true)}
                  className="rounded-card bg-[#4353E8] px-4 py-2 text-body font-semibold text-white transition-transform duration-cc ease-cc-ease hover:translate-y-[-1px]"
                >
                  Add your showcase
                </button>
              )}
            </div>

            {mine && (
              <div className="mt-4" data-testid="own-submission">
                <OwnSubmission sub={mine} onEdit={() => setUploadOpen(true)} />
              </div>
            )}

            {showState === "loading" && (
              <div data-testid="showcases-loading" className="mt-8 text-body text-ink-muted">Loading showcases…</div>
            )}
            {showState === "error" && (
              <div data-testid="showcases-error" className="mt-8 text-body text-ink-muted">Couldn't load showcases.</div>
            )}
            {showState === "ready" && results.length === 0 && (
              <div data-testid="showcases-empty" className="mt-6 rounded-card border border-ink-chip bg-ink-surface p-6 text-body text-ink-muted">
                {auth.user ? (
                  <>Nothing here yet. Be the first collector to show your <span className="text-ink-text">{variant.variant_label ? `${variant.variant_label} ` : ""}{card.name}</span>.</>
                ) : (
                  <>No approved showcases yet. Sign in with Google to contribute one.</>
                )}
              </div>
            )}

            {showState === "ready" && showPage === 1 && hero && (
              <div className="mt-6" data-testid="showcase-hero-wrap">
                <Showcase
                  entry={hero}
                  variant="hero"
                  viewer={auth.user}
                  onVoteChange={() => loadShowcases()}
                  permalink={_buildShowcasePermalink({
                    game, lang, set, card, variant, defaultVariantSlug: detail.default_variant_slug, submissionId: hero.id,
                  })}
                  openLightboxOnClick={true}
                />
              </div>
            )}

            {showState === "ready" && rest.length > 0 && (
              <div
                data-testid="showcase-tile-grid"
                className={
                  showPage === 1
                    ? "mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2"
                    : "mt-6 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4"
                }
              >
                {rest.map((entry) => (
                  <Showcase
                    key={entry.id}
                    entry={entry}
                    variant="tile"
                    viewer={auth.user}
                    onVoteChange={() => loadShowcases()}
                    permalink={_buildShowcasePermalink({
                      game, lang, set, card, variant, defaultVariantSlug: detail.default_variant_slug, submissionId: entry.id,
                    })}
                    openLightboxOnClick={true}
                  />
                ))}
              </div>
            )}

            <Pagination
              page={showPage}
              totalPages={totalPages}
              onChange={goPage}
              testId="showcases-pagination"
            />
          </section>
        </div>
      )}

      {card && (
        <Lightbox
          open={refLightbox}
          item={refLightbox && card.reference_image_url ? {
            kind: "image",
            src: card.reference_image_url,
          } : null}
          onClose={() => setRefLightbox(false)}
        />
      )}

      {card && variant && (
        <UploadModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          card={card}
          variant={variant}
          existing={mine}
          onSubmitted={(sub) => { setMine(sub); loadShowcases(); }}
        />
      )}
    </div>
  );
}

function CardIdentityHeader({ card, variant, set, game, lang, onOpenLightbox }) {
  const releaseYear =
    typeof set?.release_date === "string" && set.release_date.length >= 4
      ? set.release_date.slice(0, 4)
      : null;

  const priceLabel =
    variant.effective_price != null
      ? `$${Number(variant.effective_price).toFixed(2)}`
      : "NA";
  const priceSourceLabel = _priceSourceLabel(variant);

  const numberLabel =
    formatCardNumber({
      localId: card.number,
      cardCountOfficial: set ? set.card_count_official : null,
      setCode: set ? set.code || set.external_id : null,
    }) || "NA";
  const rarityLabel = card.rarity || "NA";
  const variantLabel = variant.variant_label || "";

  return (
    <section
      data-testid="card-identity-header"
      className="rounded-card border border-ink-chip bg-ink-surface p-4 sm:p-5"
    >
      {/* Top row: thumbnail + identity block (name / variant / set·year /
          number). On mobile the meta stack (rarity / price / caption)
          drops below this row so it uses the full section width instead
          of being cramped into the narrow right column. On desktop, the
          original 3-column meta grid is retained inside the right
          column — see the `hidden md:grid` block below. */}
      <div className="flex items-start gap-4 sm:gap-5">
        <IdentityThumb card={card} setName={set?.name} onOpenLightbox={onOpenLightbox} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h1
              data-testid="card-identity-name"
              className="text-title-section md:text-title-page text-ink-text"
            >
              {card.name}
            </h1>
            {variantLabel && (
              <span
                data-testid="card-identity-variant"
                className="text-body text-ink-muted"
              >
                · {variantLabel}
              </span>
            )}
          </div>

          <div
            data-testid="card-identity-set-year"
            className="mt-1 text-meta md:text-body text-ink-muted"
          >
            {set ? (
              <Link
                to={setPath(game, lang, set.name || set.external_id)}
                className="hover:text-indigo-text"
              >
                {set.name}
                {releaseYear ? ` · ${releaseYear}` : ""}
              </Link>
            ) : (
              releaseYear || ""
            )}
          </div>

          {/* Mobile-only inline card number — sits with the identity
              block per spec item 1 so it stays close to name/set·year. */}
          <div
            data-testid="card-identity-number-mobile"
            className="mt-2 text-meta text-ink-muted md:hidden"
          >
            <span className="text-eyebrow uppercase">No.</span>{" "}
            <span className="text-ink-text tabular">{numberLabel}</span>
          </div>

          {/* Desktop-only 3-col meta grid — untouched from the original
              arrangement. */}
          <dl
            data-testid="card-identity-meta"
            className="mt-3 hidden grid-cols-3 gap-3 border-t border-ink-chip pt-3 sm:gap-5 md:grid"
          >
            <MetaCell
              testId="card-identity-number"
              label="Number"
              value={numberLabel}
            />
            <MetaCell
              testId="card-identity-rarity"
              label="Rarity"
              value={rarityLabel}
            />
            <MetaCell
              testId="card-identity-price"
              label="Market price"
              value={priceLabel}
              sub={priceSourceLabel}
            />
          </dl>
        </div>
      </div>

      {/* Mobile-only stacked meta — rarity on its own line, market
          price prominent on its own line, quiet source/date caption
          directly under the price. Sits below the thumbnail row so
          every value shares the same left edge across the full
          section width (spec items 2–4). Hidden on md+ where the
          desktop 3-col grid above takes over. */}
      <div
        data-testid="card-identity-meta-mobile"
        className="mt-4 border-t border-ink-chip pt-3 md:hidden"
      >
        <div className="text-eyebrow uppercase text-ink-muted">Rarity</div>
        <div
          data-testid="card-identity-rarity-mobile"
          className="mt-1 text-body text-ink-text tabular"
        >
          {rarityLabel}
        </div>

        <div className="mt-3 text-eyebrow uppercase text-ink-muted">
          Market price
        </div>
        <div
          data-testid="card-identity-price-mobile"
          className="mt-1 text-title-section font-normal tracking-normal text-ink-text tabular"
        >
          {priceLabel}
        </div>
        {priceSourceLabel && (
          <div
            data-testid="card-identity-price-source-mobile"
            className="mt-0.5 text-eyebrow text-ink-muted"
          >
            {priceSourceLabel}
          </div>
        )}
      </div>
    </section>
  );
}

function _priceSourceLabel(variant) {
  if (variant.market_price != null) {
    const asOf = variant.market_price_updated_at
      ? ` · ${variant.market_price_updated_at.slice(0, 10)}`
      : "";
    const sub = variant.market_price_variant ? ` · ${variant.market_price_variant}` : "";
    return `TCGplayer${sub}${asOf}`;
  }
  if (variant.admin_substitute_price != null) return "admin substitute";
  return null;
}

/**
 * Build the canonical showcase URL from card-page context. Returns
 * null if any required piece is missing (Showcase component then
 * renders without an anchor wrapper).
 */
function _buildShowcasePermalink({ game, lang, set, card, variant, defaultVariantSlug, submissionId }) {
  if (!set?.name || !card?.name || !card?.number || !variant || !submissionId) return null;
  return showcasePath({
    game,
    lang,
    setName: set.name,
    cardName: card.name,
    number: card.number,
    variantSlug: variant.slug || variant.variant_type,
    defaultVariantSlug,
    submissionId,
  });
}

function IdentityThumb({ card, setName, onOpenLightbox }) {
  const src = card.reference_thumbnail_url || card.reference_image_url;
  const canOpenLightbox = !!card.reference_image_url;

  const sizeCls =
    "shrink-0 w-[84px] md:w-[120px] overflow-hidden rounded-chip " +
    "border border-ink-chip bg-ink-bg";
  const aspectStyle = { aspectRatio: "5 / 7" };

  const altText = setName
    ? `${card.name} from ${setName}`
    : `${card.name} reference`;

  if (!src) {
    return (
      <div
        data-testid="card-identity-thumb-placeholder"
        className={sizeCls}
        style={aspectStyle}
      >
        <CardPlaceholder label="reference not cached" />
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="card-identity-thumb-button"
      onClick={canOpenLightbox ? onOpenLightbox : undefined}
      aria-label={`Open reference image for ${card.name}`}
      className={
        sizeCls +
        " group cursor-zoom-in transition-colors duration-cc " +
        "ease-cc-ease hover:border-ink-muted"
      }
      style={aspectStyle}
    >
      <img
        data-testid="card-identity-thumb-image"
        src={src}
        alt={altText}
        loading="lazy"
        className="block h-full w-full object-cover"
      />
    </button>
  );
}

function MetaCell({ testId, label, value, sub }) {
  return (
    <div>
      <dt className="text-eyebrow uppercase text-ink-muted">{label}</dt>
      <dd
        data-testid={testId}
        className="mt-1 text-body text-ink-text tabular"
      >
        {value}
      </dd>
      {sub && (
        <div className="mt-0.5 text-eyebrow text-ink-muted">{sub}</div>
      )}
    </div>
  );
}


function OtherPrintings({
  game, lang, setName, cardName, number, defaultVariantSlug, siblings, activeSlug,
}) {
  return (
    <div
      data-testid="other-printings"
      className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1"
    >
      <span className="text-eyebrow uppercase text-ink-muted">
        Other printings
      </span>
      <ul className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {siblings.map((s) => (
          <li key={s.slug}>
            <Link
              data-testid={`other-printing-${s.slug}`}
              to={cardPath({
                game, lang, setName, cardName, number,
                variantSlug: s.slug,
                defaultVariantSlug,
              })}
              className="text-meta text-indigo-text hover:underline"
            >
              {s.variant_label || "Normal"}
              {typeof s.submission_count === "number" && s.submission_count > 0 && (
                <span className="ml-1 text-ink-muted tabular">
                  ({s.submission_count})
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}


function OwnSubmission({ sub, onEdit }) {
  const status = sub.status || "pending";
  const chipColor = status === "approved"
    ? "border-[#254a2b] bg-[#122019] text-[#8FE3A1]"
    : status === "rejected"
      ? "border-[#4a1e1e] bg-[#2a1414] text-[#F4A6A6]"
      : "border-indigo-chip-border bg-indigo-chip-bg text-indigo-chip-text";
  const transcode = sub.video?.transcode_status;
  return (
    <div className="rounded-card border border-ink-chip bg-ink-raised p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className={`rounded-chip border px-2 py-0.5 text-eyebrow uppercase ${chipColor}`} data-testid="own-submission-status">
            {status}
          </div>
          <span className="text-meta text-ink-muted">Your submission</span>
        </div>
        <button
          data-testid="own-submission-edit"
          onClick={onEdit}
          className="text-meta text-indigo-text hover:underline"
        >
          Edit / replace
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {sub.image?.url && (
          <a href={sub.image.url} target="_blank" rel="noreferrer">
            <img
              src={sub.image.thumbnail_url || sub.image.url}
              alt="your photo"
              className="w-full rounded-chip object-cover"
              style={{ aspectRatio: "1 / 1" }}
            />
          </a>
        )}
        {sub.video && (
          <div className="rounded-chip border border-ink-chip bg-ink-bg p-2 text-meta text-ink-muted">
            {transcode === "ready" && sub.video.poster_url && (
              <img src={sub.video.poster_url} alt="video poster"
                   className="w-full rounded-chip object-cover"
                   style={{ aspectRatio: "16 / 9" }} />
            )}
            <div className="mt-2 flex items-center justify-between">
              <span data-testid="own-video-transcode-status">
                Video: {transcode || "queued"}
              </span>
              {transcode === "ready" && (
                <a
                  href={sub.video.preview_url || sub.video.mp4_url}
                  target="_blank" rel="noreferrer"
                  className="text-indigo-text hover:underline"
                >
                  Preview
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
